import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import attachmentApi from "app/services/http-services/attachment/attachment-api";

const attachmentQueryKey = (tableName: string, recordId?: string | null, columnName?: string | null) => [
    "attachments",
    tableName,
    recordId || "",
    columnName || "record",
];

const draftAttachmentQueryKey = (tableName: string, draftToken?: string | null, columnName?: string | null) => [
    "draft-attachments",
    tableName,
    draftToken || "",
    columnName || "record",
];

export const useAttachments = (
    tableName: string,
    recordId?: string | null,
    columnName?: string | null,
    draftToken?: string | null
) => {
    const queryClient = useQueryClient();
    const useDraft = Boolean(tableName && !recordId && draftToken);
    const enabled = Boolean(tableName && (recordId || draftToken));
    const queryKey = useDraft
        ? draftAttachmentQueryKey(tableName, draftToken, columnName)
        : attachmentQueryKey(tableName, recordId, columnName);

    const query = useQuery({
        queryKey,
        enabled,
        queryFn: () => useDraft
            ? attachmentApi.listDraftAttachments(tableName, draftToken!, columnName)
            : attachmentApi.listAttachments(tableName, recordId!, columnName),
    });

    const upload = useMutation({
        mutationFn: (file: File) => useDraft
            ? attachmentApi.uploadDraftAttachment(tableName, draftToken!, file, columnName)
            : attachmentApi.uploadAttachment(tableName, recordId!, file, columnName),
        onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    });

    const remove = useMutation({
        mutationFn: (attachmentId: string) => useDraft
            ? attachmentApi.deleteDraftAttachment(tableName, draftToken!, attachmentId, columnName)
            : attachmentApi.deleteAttachment(tableName, recordId!, attachmentId, columnName),
        onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    });

    return {
        ...query,
        attachments: query.data || [],
        upload,
        remove,
        canUse: enabled,
    };
};

export const buildAttachmentDownloadUrl = attachmentApi.downloadAttachmentUrl;
export const buildDraftAttachmentDownloadUrl = attachmentApi.downloadDraftAttachmentUrl;
